import { useNavigate } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  CardholderInformationDocument,
  CardholderInformationQuery,
  MyCardsDocument,
  RequestSingleUseBankingCardCardholderDataInput,
  RequestSingleUseCardDocument,
  RequestSingleUseCardMutation,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

// Hooks
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
// Components
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import Content from 'modules/expenses/components/cards/PageContents/RequestCardContent'
import { getPhoneCode } from 'modules/expenses/lib/countryCodes'
import { isMobilePhoneNumber, isValidPhoneNumber } from 'modules/expenses/lib/phoneNumber'

import { RequestCardForm } from './forms'

const RequestCardPage = observer(() => {
  const { employee, company } = useCurrentSession()
  const { data: cardholderInformation } = useQuery(CardholderInformationDocument, {
    variables: {
      employeeId: Number(employee?.id),
      creationSource: 'expenses',
    },
    skip: !employee,
  })

  const currentInformation = cardholderInformation?.employees?.employee
  const cardholder = currentInformation?.bankingCardholder
  const cardholderExists = !!cardholder
  const [requestCard, { loading: requestCardLoading }] = useMutation(RequestSingleUseCardDocument, {
    extractorFn: (data: RequestSingleUseCardMutation) =>
      data.bankingMutations.requestSingleUseBankingCard,
  })
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const onClose = () => {
    return () => {
      navigate(navigation.myCards.fullPath())
    }
  }

  const formValuesFromCurrentInformation = (
    currentInformation?: CardholderInformationQuery['employees']['employee']
  ) => {
    return {
      alias: '',
      spendingAmount: '',
      dateOfBirth: currentInformation?.birthdayOn || '',
      firstName: currentInformation?.firstName || '',
      lastName: currentInformation?.lastName || '',
      phoneCountryCode:
        cardholder?.phoneCountryCode ||
        currentInformation?.phoneCode ||
        getPhoneCode(currentInformation?.country) ||
        '',
      phoneNumber: cardholder?.phoneNumber || currentInformation?.phoneNumber || '',
      description: '',
    }
  }

  const form = useForm<RequestCardForm>({
    defaultValues: formValuesFromCurrentInformation(currentInformation),
    validateOnBlur: true,
    validateOnChange: false,
    validateOnMount: false,
    validate: (data) => {
      const errors: FormErrors<RequestCardForm> = {}

      if (!data.spendingAmount) {
        errors.spendingAmount = i18n.t(
          'software_management.software_setups.cards.errors.spending_amount_mandatory'
        )
      }

      if (parseInt(data.spendingAmount) < 0) {
        errors.spendingAmount = i18n.t(
          'software_management.software_setups.cards.errors.spending_amount_positive'
        )
      }

      if (!data.description) {
        errors.description = i18n.t('common.errors.required')
      }

      if (!cardholderExists) {
        const fullName = `${data.firstName} ${data.lastName}`

        if (fullName.length > 24) {
          errors.firstName = i18n.t('expenses_v2.forms.errors.full_name.too_long')
          errors.lastName = i18n.t('expenses_v2.forms.errors.full_name.too_long')
        }

        if (!isMobilePhoneNumber(data.phoneCountryCode, data.phoneNumber)) {
          errors.phoneNumber = i18n.t('expenses_v2.forms.errors.phone.not_mobile')
        } else if (!isValidPhoneNumber(data.phoneCountryCode, data.phoneNumber)) {
          errors.phoneNumber = i18n.t('expenses_v2.forms.errors.phone.invalid')
        }

        if (!data.dateOfBirth) {
          errors.dateOfBirth = i18n.t(
            'software_management.software_setups.cards.errors.birth_date_mandatory'
          )
        }

        if (!data.firstName) {
          errors.firstName = i18n.t('common.errors.required')
        }

        if (!data.lastName) {
          errors.lastName = i18n.t('common.errors.required')
        }

        if (!data.phoneNumber) {
          errors.phoneNumber = i18n.t(
            'software_management.software_setups.cards.errors.mobile_phone_mandatory'
          )
        }

        if (!data.phoneCountryCode) {
          errors.phoneCountryCode = i18n.t(
            'software_management.software_setups.cards.errors.mobile_phone_mandatory'
          )
        }
      }
      return errors
    },
    onSubmit: async (data, { setErrors }) => {
      const amountInCents = parseInt(data.spendingAmount) * 100

      let cardholderData: RequestSingleUseBankingCardCardholderDataInput | undefined
      if (!cardholderExists) {
        cardholderData = {
          dateOfBirth: data.dateOfBirth,
          email: currentInformation?.email,
          firstName: data.firstName,
          lastName: data.lastName,
          phoneNumber: data.phoneNumber,
          phoneCountryCode: data.phoneCountryCode,
        }
      }
      const res = await requestCard({
        variables: {
          employeeId: String(employee?.id),
          alias: data.alias,
          amountLimitCents: amountInCents,
          cardholderData,
          companyId: company.id,
          description: data.description,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: MyCardsDocument,
            variables: {
              employeeId: Number(employee?.id),
              companyId: Number(company?.id),
            },
          },
        ],
      })

      const result = handleResult(res, ({ spendingAmount }) => {
        setErrors({
          spendingAmount,
        })
      })

      if (result.success) {
        navigate(navigation.myCards.fullPath())
        app.setFlash({
          type: 'correct',
          message: i18n.t('software_management.software_setups.cards.alerts.request_success'),
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('software_management.software_setups.cards.errors.error_request'),
        })
      }

      return result
    },
  })
  const submitDisabled = !form.dirty || form.isSubmitting || !form.isValid || requestCardLoading

  useUnsafeEffect(() => {
    form.setValues(formValuesFromCurrentInformation(currentInformation))
  }, [cardholderInformation])

  return (
    <Box width="full" flexDirection="row" justifyContent="center">
      <Box width="narrowPage">
        <Content currentInformation={currentInformation} form={form} />
        <Box paddingX="s32" justifyContent="spaceBetween" flexDirection="row">
          <Button onClick={onClose()}>{i18n.t('expenses_v2.cards.form.cancel')}</Button>
          <Button disabled={submitDisabled} onClick={() => form.handleSubmit()}>
            {i18n.t('expenses_v2.buttons.request_single_use_card')}
          </Button>
        </Box>
      </Box>
    </Box>
  )
})

export default RequestCardPage
